/**
 * Team coordination storage service
 *
 * Handles team management, membership coordination, and trip member management
 * with offline-first patterns and comprehensive error handling.
 */

import { db } from '../database/schema.js'
import type { Team, TeamMember, TripMember } from '../database/schema.js'
import {
  BaseStorageService,
  OfflineFirstStorageService,
  type StorageResult,
} from './base-storage.js'
import { ValidationUtils, type ValidationError } from './validation.js'

/**
 * Team creation data
 */
export interface CreateTeamData {
  name: string
  description: string
  descriptionRich?: { type: 'doc'; content?: unknown[] }
  logo?: string
  emergencyContact?: { name: string; phone: string }
  createdBy: string
}

/**
 * Team member invitation data
 */
export interface InviteTeamMemberData {
  teamId: string
  userId: string
  role: TeamMember['role']
  invitedBy: string
}

/**
 * Trip member invitation data
 */
export interface InviteTripMemberData {
  tripId: string
  userId: string
  role: TripMember['role']
  invitedBy: string
}

/**
 * Team with member details
 */
export interface TeamWithMembers extends Team {
  members: TeamMemberWithUser[]
  memberCount: number
  ownerCount: number
}

/**
 * Team member with user details
 */
export interface TeamMemberWithUser extends TeamMember {
  user?:
    | {
        id: string
        displayName: string
        email: string
        bio?: string | undefined
        certifications?: string[] | undefined
      }
    | undefined
}

/**
 * Trip member with user details
 */
export interface TripMemberWithUser extends TripMember {
  user?:
    | {
        id: string
        displayName: string
        email: string
        bio?: string | undefined
        certifications?: string[] | undefined
      }
    | undefined
}

/**
 * Team search filters
 */
export interface TeamSearchFilters {
  createdBy?: string
  hasUser?: string
}

/**
 * Team validation
 */
function validateTeam(
  data: Partial<Team>,
  context: 'create' | 'update' = 'create'
): { isValid: boolean; errors: ValidationError[] } {
  const errors: ValidationError[] = []

  if (context === 'create') {
    if (!data.name || !ValidationUtils.isValidStringLength(data.name, 1, 100)) {
      errors.push(
        ValidationUtils.createError('name', 'Team name must be 1-100 characters', 'INVALID_LENGTH')
      )
    }

    if (data.description && !ValidationUtils.isValidStringLength(data.description, 0, 500)) {
      errors.push(
        ValidationUtils.createError(
          'description',
          'Description must be under 500 characters',
          'INVALID_LENGTH'
        )
      )
    }

    if (!data.createdBy || !ValidationUtils.isValidUUID(data.createdBy)) {
      errors.push(ValidationUtils.createError('createdBy', 'Invalid creator ID', 'INVALID_FORMAT'))
    }
  }

  return { isValid: errors.length === 0, errors }
}

/**
 * Team member validation
 */
function validateTeamMember(
  data: Partial<TeamMember>,
  context: 'create' | 'update' = 'create'
): { isValid: boolean; errors: ValidationError[] } {
  const errors: ValidationError[] = []

  if (context === 'create') {
    if (!data.teamId || !ValidationUtils.isValidUUID(data.teamId)) {
      errors.push(ValidationUtils.createError('teamId', 'Invalid team ID', 'INVALID_FORMAT'))
    }

    if (!data.userId || !ValidationUtils.isValidUUID(data.userId)) {
      errors.push(ValidationUtils.createError('userId', 'Invalid user ID', 'INVALID_FORMAT'))
    }

    if (!data.role || !['owner', 'member'].includes(data.role)) {
      errors.push(ValidationUtils.createError('role', 'Invalid role', 'INVALID_VALUE'))
    }
  }

  if (data.status && !['active', 'inactive'].includes(data.status)) {
    errors.push(ValidationUtils.createError('status', 'Invalid status', 'INVALID_VALUE'))
  }

  return { isValid: errors.length === 0, errors }
}

/**
 * Trip member validation
 */
function validateTripMember(
  data: Partial<TripMember>,
  context: 'create' | 'update' = 'create'
): { isValid: boolean; errors: ValidationError[] } {
  const errors: ValidationError[] = []

  if (context === 'create') {
    if (!data.tripId || !ValidationUtils.isValidUUID(data.tripId)) {
      errors.push(ValidationUtils.createError('tripId', 'Invalid trip ID', 'INVALID_FORMAT'))
    }

    if (!data.userId || !ValidationUtils.isValidUUID(data.userId)) {
      errors.push(ValidationUtils.createError('userId', 'Invalid user ID', 'INVALID_FORMAT'))
    }

    if (!data.role || !['leader', 'member', 'viewer'].includes(data.role)) {
      errors.push(ValidationUtils.createError('role', 'Invalid role', 'INVALID_VALUE'))
    }
  }

  if (data.status && !['invited', 'confirmed', 'declined'].includes(data.status)) {
    errors.push(ValidationUtils.createError('status', 'Invalid status', 'INVALID_VALUE'))
  }

  return { isValid: errors.length === 0, errors }
}

/**
 * Team storage service
 */
export class TeamStorageService extends OfflineFirstStorageService<Team> {
  constructor() {
    super(db.teams, 'team')
  }

  /**
   * Create a new team with validation
   */
  async createTeam(teamData: CreateTeamData): Promise<StorageResult<Team>> {
    const validation = validateTeam(teamData, 'create')
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    return this.create(teamData)
  }

  /**
   * Update team with validation
   */
  async updateTeam(id: string, updates: Partial<Team>): Promise<StorageResult<Team>> {
    const validation = validateTeam(updates, 'update')
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    return this.update(id, updates)
  }

  /**
   * Get team with member details
   */
  async getTeamWithMembers(teamId: string): Promise<StorageResult<TeamWithMembers | undefined>> {
    return this.executeOperation(async () => {
      const team = await this.table.get(teamId)
      if (!team) {
        return undefined
      }

      const members = await db.teamMembers.where('teamId').equals(teamId).toArray()
      const userIds = members.map(m => m.userId)
      const users = await db.users.where('id').anyOf(userIds).toArray()

      const membersWithUser: TeamMemberWithUser[] = members.map(member => ({
        ...member,
        user: users.find(u => u.id === member.userId) ?? undefined,
      }))

      const teamWithMembers: TeamWithMembers = {
        ...team,
        members: membersWithUser,
        memberCount: members.length,
        ownerCount: members.filter(m => m.role === 'owner').length,
      }

      return teamWithMembers
    }, 'get team with members')
  }

  /**
   * Get teams for a user
   */
  async getTeamsForUser(
    userId: string,
    filters: TeamSearchFilters = {}
  ): Promise<StorageResult<Team[]>> {
    return this.executeOperation(async () => {
      const memberships = await db.teamMembers
        .where('userId')
        .equals(userId)
        .and(member => member.status === 'active')
        .toArray()

      const teamIds = memberships.map(m => m.teamId)

      if (teamIds.length === 0) {
        return []
      }

      let teams = await this.table.where('id').anyOf(teamIds).toArray()

      if (filters.createdBy) {
        teams = teams.filter(team => team.createdBy === filters.createdBy)
      }

      return teams
    }, 'get teams for user')
  }

  /**
   * Get teams created by user
   */
  async getTeamsCreatedByUser(userId: string): Promise<StorageResult<Team[]>> {
    return this.executeOperation(
      () => this.table.where('createdBy').equals(userId).toArray(),
      'get teams created by user'
    )
  }

  /**
   * Check if user is team owner
   */
  async isUserTeamOwner(teamId: string, userId: string): Promise<StorageResult<boolean>> {
    return this.executeOperation(async () => {
      const membership = await db.teamMembers
        .where('[teamId+userId]')
        .equals([teamId, userId])
        .first()

      return membership?.role === 'owner' && membership.status === 'active'
    }, 'check user team owner')
  }
}

/**
 * Team member storage service
 */
export class TeamMemberStorageService extends BaseStorageService<TeamMember & { id?: string }> {
  constructor() {
    super(db.teamMembers, 'teamMember')
  }

  /**
   * Invite user to team
   */
  async inviteToTeam(inviteData: InviteTeamMemberData): Promise<StorageResult<TeamMember>> {
    const validation = validateTeamMember(
      {
        teamId: inviteData.teamId,
        userId: inviteData.userId,
        role: inviteData.role,
      },
      'create'
    )

    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    return this.executeOperation(async () => {
      // Check if user is already a member
      const existing = await this.table
        .where('[teamId+userId]')
        .equals([inviteData.teamId, inviteData.userId])
        .first()

      if (existing) {
        throw new Error('User is already a team member')
      }

      const now = new Date().toISOString()

      const teamMember: TeamMember = {
        teamId: inviteData.teamId,
        userId: inviteData.userId,
        role: inviteData.role,
        status: 'active',
        joinedAt: now,
      }

      await this.table.add(teamMember)
      return teamMember
    }, 'invite to team')
  }

  /**
   * Update team member role
   */
  async updateMemberRole(
    teamId: string,
    userId: string,
    role: TeamMember['role']
  ): Promise<StorageResult<TeamMember>> {
    return this.executeOperation(async () => {
      const member = await this.table.where('[teamId+userId]').equals([teamId, userId]).first()

      if (!member) {
        throw new Error('Team member not found')
      }

      const updatedMember = {
        ...member,
        role,
        updatedAt: new Date().toISOString(),
      }

      await this.table.put(updatedMember)
      return updatedMember
    }, 'update member role')
  }

  /**
   * Remove team member
   */
  async removeMember(teamId: string, userId: string): Promise<StorageResult<void>> {
    return this.executeOperation(async () => {
      await this.table.where('[teamId+userId]').equals([teamId, userId]).delete()
      return undefined
    }, 'remove team member')
  }

  /**
   * Get team members with user details
   */
  async getTeamMembersWithUsers(teamId: string): Promise<StorageResult<TeamMemberWithUser[]>> {
    return this.executeOperation(async () => {
      const members = await this.table.where('teamId').equals(teamId).toArray()
      const userIds = members.map(m => m.userId)
      const users = await db.users.where('id').anyOf(userIds).toArray()

      const membersWithUser: TeamMemberWithUser[] = members.map(member => ({
        ...member,
        user: users.find(u => u.id === member.userId),
      }))

      return membersWithUser
    }, 'get team members with users')
  }

  /**
   * Get user's team membership
   */
  async getUserTeamMembership(
    teamId: string,
    userId: string
  ): Promise<StorageResult<TeamMember | undefined>> {
    return this.executeOperation(
      () => this.table.where('[teamId+userId]').equals([teamId, userId]).first(),
      'get user team membership'
    )
  }
}

/**
 * Trip member storage service
 */
export class TripMemberStorageService extends BaseStorageService<TripMember & { id?: string }> {
  constructor() {
    super(db.tripMembers, 'tripMember')
  }

  /**
   * Invite user to trip
   */
  async inviteToTrip(inviteData: InviteTripMemberData): Promise<StorageResult<TripMember>> {
    const validation = validateTripMember(
      {
        tripId: inviteData.tripId,
        userId: inviteData.userId,
        role: inviteData.role,
      },
      'create'
    )

    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    return this.executeOperation(async () => {
      // Check if user is already a member
      const existing = await this.table
        .where('[tripId+userId]')
        .equals([inviteData.tripId, inviteData.userId])
        .first()

      if (existing) {
        throw new Error('User is already a trip member')
      }

      const now = new Date().toISOString()

      const tripMember: TripMember = {
        tripId: inviteData.tripId,
        userId: inviteData.userId,
        role: inviteData.role,
        status: 'invited',
        joinedAt: now,
      }

      await this.table.add(tripMember)
      return tripMember
    }, 'invite to trip')
  }

  /**
   * Accept trip invitation
   */
  async acceptInvitation(tripId: string, userId: string): Promise<StorageResult<TripMember>> {
    return this.executeOperation(async () => {
      const member = await this.table.where('[tripId+userId]').equals([tripId, userId]).first()

      if (!member) {
        throw new Error('Trip invitation not found')
      }

      if (member.status !== 'invited') {
        throw new Error('Invitation already processed')
      }

      const updatedMember = {
        ...member,
        status: 'confirmed' as const,
        updatedAt: new Date().toISOString(),
      }

      await this.table.put(updatedMember)
      return updatedMember
    }, 'accept trip invitation')
  }

  /**
   * Decline trip invitation
   */
  async declineInvitation(tripId: string, userId: string): Promise<StorageResult<TripMember>> {
    return this.executeOperation(async () => {
      const member = await this.table.where('[tripId+userId]').equals([tripId, userId]).first()

      if (!member) {
        throw new Error('Trip invitation not found')
      }

      if (member.status !== 'invited') {
        throw new Error('Invitation already processed')
      }

      const updatedMember = {
        ...member,
        status: 'declined' as const,
        updatedAt: new Date().toISOString(),
      }

      await this.table.put(updatedMember)
      return updatedMember
    }, 'decline trip invitation')
  }

  /**
   * Update trip member role
   */
  async updateMemberRole(
    tripId: string,
    userId: string,
    role: TripMember['role']
  ): Promise<StorageResult<TripMember>> {
    return this.executeOperation(async () => {
      const member = await this.table.where('[tripId+userId]').equals([tripId, userId]).first()

      if (!member) {
        throw new Error('Trip member not found')
      }

      const updatedMember = {
        ...member,
        role,
        updatedAt: new Date().toISOString(),
      }

      await this.table.put(updatedMember)
      return updatedMember
    }, 'update trip member role')
  }

  /**
   * Remove trip member
   */
  async removeMember(tripId: string, userId: string): Promise<StorageResult<void>> {
    return this.executeOperation(async () => {
      await this.table.where('[tripId+userId]').equals([tripId, userId]).delete()
      return undefined
    }, 'remove trip member')
  }

  /**
   * Get trip members with user details
   */
  async getTripMembersWithUsers(tripId: string): Promise<StorageResult<TripMemberWithUser[]>> {
    return this.executeOperation(async () => {
      const members = await this.table.where('tripId').equals(tripId).toArray()
      const userIds = members.map(m => m.userId)
      const users = await db.users.where('id').anyOf(userIds).toArray()

      const membersWithUser: TripMemberWithUser[] = members.map(member => ({
        ...member,
        user: users.find(u => u.id === member.userId),
      }))

      return membersWithUser
    }, 'get trip members with users')
  }

  /**
   * Get confirmed trip members
   */
  async getConfirmedTripMembers(tripId: string): Promise<StorageResult<TripMemberWithUser[]>> {
    return this.executeOperation(async () => {
      const members = await this.table
        .where('tripId')
        .equals(tripId)
        .and(member => member.status === 'confirmed')
        .toArray()

      const userIds = members.map(m => m.userId)
      const users = await db.users.where('id').anyOf(userIds).toArray()

      const membersWithUser: TripMemberWithUser[] = members.map(member => ({
        ...member,
        user: users.find(u => u.id === member.userId),
      }))

      return membersWithUser
    }, 'get confirmed trip members')
  }

  /**
   * Get user's trip membership
   */
  async getUserTripMembership(
    tripId: string,
    userId: string
  ): Promise<StorageResult<TripMember | undefined>> {
    return this.executeOperation(
      () => this.table.where('[tripId+userId]').equals([tripId, userId]).first(),
      'get user trip membership'
    )
  }

  /**
   * Get pending invitations for user
   */
  async getPendingInvitations(userId: string): Promise<StorageResult<TripMember[]>> {
    return this.executeOperation(
      () =>
        this.table
          .where('userId')
          .equals(userId)
          .and(member => member.status === 'invited')
          .toArray(),
      'get pending invitations'
    )
  }

  /**
   * Check if user can modify trip
   */
  async canUserModifyTrip(tripId: string, userId: string): Promise<StorageResult<boolean>> {
    return this.executeOperation(async () => {
      const membership = await this.table.where('[tripId+userId]').equals([tripId, userId]).first()

      return membership?.role === 'leader' && membership.status === 'confirmed'
    }, 'check user modify trip permissions')
  }
}

// Create and export singleton instances
export const teamStorage = new TeamStorageService()
export const teamMemberStorage = new TeamMemberStorageService()
export const tripMemberStorage = new TripMemberStorageService()
